import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import api, { notifyUpdate } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Building2, Image as ImageIcon, CalendarPlus, TrendingUp, Plus, X } from 'lucide-react';

export default function MojObjekat() {
  const { user } = useAuth();
  const [offers, setOffers] = useState([]);
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(null);
  const [newImg, setNewImg] = useState('');
  const [eventForm, setEventForm] = useState({ title: '', date: '', time: '18:00', location: '', description: '' });

  useEffect(() => {
    const load = async () => {
      const results = await Promise.allSettled([api.get('/offers/mine'), api.get('/events/mine')]);
      const val = (r) => (r.status === 'fulfilled' ? r.value.data : []);
      setOffers(val(results[0]));
      setEvents(val(results[1]));
    };
    load();
    window.addEventListener('store-updated', load);
    return () => window.removeEventListener('store-updated', load);
  }, [user]);

  const myOffer = offers[0];

  useEffect(() => {
    if (myOffer && !form) setForm({ ...myOffer });
  }, [myOffer, form]);

  if (!myOffer) {
    return (
      <div className="bg-white rounded-xl border border-slate-100 p-12 text-center" data-testid="no-object">
        <Building2 className="h-10 w-10 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500">Vaš račun još nema povezan objekat. Kontaktirajte administratora sistema.</p>
      </div>
    );
  }

  const requestUpdate = async () => {
    try {
      await api.post('/change-requests', {
        description: `Izmjena podataka objekta „${form.title}“`,
        payload: form,
        offerId: myOffer.id
      });
      toast.success('Zahtjev za izmjenu podataka je poslan administratoru na odobrenje.');
      notifyUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Greška pri slanju zahtjeva.');
    }
  };

  const addImage = async () => {
    if (!newImg.startsWith('http')) { toast.error('Unesite ispravan URL slike.'); return; }
    try {
      await api.put(`/offers/${myOffer.id}`, { gallery: [...myOffer.gallery, newImg] });
      setNewImg('');
      toast.success('Slika je dodana u galeriju.');
      notifyUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Greška pri dodavanju slike.');
    }
  };

  const removeImage = async (idx) => {
    try {
      await api.put(`/offers/${myOffer.id}`, { gallery: myOffer.gallery.filter((_, i) => i !== idx) });
      toast.success('Slika je uklonjena.');
      notifyUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Greška pri uklanjanju slike.');
    }
  };

  const addEvent = async (e) => {
    e.preventDefault();
    if (!eventForm.title || !eventForm.date || !eventForm.description) { toast.error('Popunite naziv, datum i opis.'); return; }
    try {
      await api.post('/events', {
        ...eventForm, endDate: eventForm.date, city: myOffer.city, location: eventForm.location || myOffer.title,
        lat: myOffer.lat, lng: myOffer.lng, image: myOffer.image, category: 'Ostalo'
      });
      setEventForm({ title: '', date: '', time: '18:00', location: '', description: '' });
      toast.success('Događaj je poslan administratoru na odobrenje.');
      notifyUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Greška pri spremanju događaja.');
    }
  };

  return (
    <div data-testid="moj-objekat">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#0b1a3d]">Moj objekat</h1>
        <p className="text-sm text-slate-500">Upravljanje podacima objekta „{myOffer.title}“. Izmjene podataka zahtijevaju odobrenje administratora.</p>
      </div>

      <Tabs defaultValue="podaci">
        <TabsList className="bg-[#e8ecf7] h-10 mb-5 flex-wrap">
          <TabsTrigger value="podaci" data-testid="tab-podaci" className="data-[state=active]:bg-[#001752] data-[state=active]:text-white px-5">
            <Building2 className="h-4 w-4 mr-1.5" /> Podaci
          </TabsTrigger>
          <TabsTrigger value="galerija" data-testid="tab-galerija" className="data-[state=active]:bg-[#001752] data-[state=active]:text-white px-5">
            <ImageIcon className="h-4 w-4 mr-1.5" /> Galerija
          </TabsTrigger>
          <TabsTrigger value="dogadjaji" data-testid="tab-moji-dogadjaji" className="data-[state=active]:bg-[#001752] data-[state=active]:text-white px-5">
            <CalendarPlus className="h-4 w-4 mr-1.5" /> Događaji
          </TabsTrigger>
          <TabsTrigger value="promet" data-testid="tab-promet" className="data-[state=active]:bg-[#001752] data-[state=active]:text-white px-5">
            <TrendingUp className="h-4 w-4 mr-1.5" /> Promet
          </TabsTrigger>
        </TabsList>

        <TabsContent value="podaci">
          {form && (
            <div className="bg-white rounded-xl border border-slate-100 p-7 space-y-4 max-w-3xl" data-testid="object-data-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-600 mb-1.5 block">Naziv objekta</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="object-title" className="border-slate-200" />
                </div>
                <div>
                  <Label className="text-slate-600 mb-1.5 block">Telefon</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="border-slate-200" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-600 mb-1.5 block">E-mail</Label>
                  <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="border-slate-200" />
                </div>
                <div>
                  <Label className="text-slate-600 mb-1.5 block">Link za rezervacije</Label>
                  <Input value={form.bookingUrl} onChange={(e) => setForm({ ...form, bookingUrl: e.target.value })} className="border-slate-200" />
                </div>
              </div>
              <div>
                <Label className="text-slate-600 mb-1.5 block">Adresa</Label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="border-slate-200" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-600 mb-1.5 block">GPS širina (lat)</Label>
                  <Input type="number" step="0.0001" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} className="border-slate-200" />
                </div>
                <div>
                  <Label className="text-slate-600 mb-1.5 block">GPS dužina (lng)</Label>
                  <Input type="number" step="0.0001" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} className="border-slate-200" />
                </div>
              </div>
              <div>
                <Label className="text-slate-600 mb-1.5 block">Opis</Label>
                <Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="border-slate-200" />
              </div>
              <Button onClick={requestUpdate} className="bg-[#001752] hover:bg-[#0a2a7a] text-white rounded-md transition-colors duration-200" data-testid="request-update-btn">
                Pošalji zahtjev za izmjenu
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="galerija">
          <div className="bg-white rounded-xl border border-slate-100 p-7" data-testid="gallery-manager">
            <div className="flex gap-2 mb-6 max-w-xl">
              <Input value={newImg} onChange={(e) => setNewImg(e.target.value)} placeholder="URL slike (https://...)" data-testid="gallery-url-input" className="border-slate-200" />
              <Button onClick={addImage} className="bg-[#001752] hover:bg-[#0a2a7a] text-white rounded-md shrink-0 transition-colors duration-200" data-testid="gallery-add-btn">
                <Plus className="h-4 w-4 mr-1.5" /> Dodaj
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {myOffer.gallery.map((g, i) => (
                <div key={i} className="relative group rounded-lg overflow-hidden h-36" data-testid={`gallery-img-${i}`}>
                  <img src={g} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => removeImage(i)} data-testid={`gallery-remove-${i}`}
                    className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="dogadjaji">
          <div className="grid lg:grid-cols-2 gap-6">
            <form onSubmit={addEvent} className="bg-white rounded-xl border border-slate-100 p-7 space-y-4" data-testid="subject-event-form">
              <h3 className="font-bold text-[#0b1a3d]">Novi događaj u objektu</h3>
              <p className="text-xs text-slate-500">Događaj će biti prikazan u kalendaru nakon odobrenja administratora sistema.</p>
              <div>
                <Label className="text-slate-600 mb-1.5 block">Naziv događaja *</Label>
                <Input value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} data-testid="subject-event-title" className="border-slate-200" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-600 mb-1.5 block">Datum *</Label>
                  <Input type="date" value={eventForm.date} onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })} data-testid="subject-event-date" className="border-slate-200" />
                </div>
                <div>
                  <Label className="text-slate-600 mb-1.5 block">Vrijeme</Label>
                  <Input type="time" value={eventForm.time} onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })} className="border-slate-200" />
                </div>
              </div>
              <div>
                <Label className="text-slate-600 mb-1.5 block">Opis *</Label>
                <Textarea rows={3} value={eventForm.description} onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })} data-testid="subject-event-desc" className="border-slate-200" />
              </div>
              <Button type="submit" className="bg-[#001752] hover:bg-[#0a2a7a] text-white rounded-md transition-colors duration-200" data-testid="subject-event-submit">
                Pošalji na odobrenje
              </Button>
            </form>

            <div className="bg-white rounded-xl border border-slate-100 p-7" data-testid="subject-events-list">
              <h3 className="font-bold text-[#0b1a3d] mb-4">Moji događaji</h3>
              <div className="space-y-3">
                {events.length === 0 && <p className="text-sm text-slate-500">Još nemate dodanih događaja.</p>}
                {events.map((e) => (
                  <div key={e.id} className="border border-slate-100 rounded-lg p-3 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-700">{e.title}</p>
                      <p className="text-xs text-slate-500">{e.date} • {e.time}h</p>
                    </div>
                    {e.status === 'approved'
                      ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Objavljen</Badge>
                      : <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Na čekanju</Badge>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="promet">
          <div className="bg-white rounded-xl border border-slate-100 p-7" data-testid="subject-traffic">
            <h3 className="font-bold text-[#0b1a3d] mb-1">Promet objekta – prijave i odjave turista</h3>
            <p className="text-xs text-slate-500 mb-6">Podaci iz sistema za prijavu i odjavu turista na nivou kantona (demo).</p>
            <div className="grid grid-cols-3 gap-4 mb-8 max-w-lg">
              {[
                { l: 'Prijava (juli)', v: '486' }, { l: 'Noćenja (juli)', v: '1.240' }, { l: 'Popunjenost', v: '78%' }
              ].map((s, i) => (
                <div key={i} className="bg-[#f8f9fd] rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-[#001752]">{s.v}</p>
                  <p className="text-xs text-slate-500 mt-1">{s.l}</p>
                </div>
              ))}
            </div>
            <div className="space-y-3 max-w-2xl">
              {[
                { m: 'Mart', v: 55 }, { m: 'April', v: 42 }, { m: 'Maj', v: 61 }, { m: 'Juni', v: 74 }, { m: 'Juli', v: 78 }
              ].map((m) => (
                <div key={m.m} className="flex items-center gap-3">
                  <span className="text-xs text-slate-500 w-14">{m.m}</span>
                  <div className="flex-1 bg-[#f0f3fb] rounded-full h-5 overflow-hidden">
                    <div className="h-full bg-[#001752] rounded-full" style={{ width: `${m.v}%` }} />
                  </div>
                  <span className="text-xs font-semibold text-[#001752] w-10">{m.v}%</span>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
